import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';
import { publicBlogList } from '../lib/api';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

export default function LatestFromBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicBlogList({ limit: 3 })
      .then((d) => setItems(d.items || []))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, []);

  // Hide the section entirely if there are no published posts yet
  if (!loading && items.length === 0) return null;

  return (
    <section id="blog-preview" className="py-14 sm:py-20" data-testid="latest-from-blog">
      <div className="container-x">
        {/* Section heading */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] font-semibold mb-2">
              <BookOpen className="h-3.5 w-3.5" />
              From the Blog
            </div>
            <h2 className="font-display uppercase text-2xl sm:text-3xl tracking-wide">
              Trading <span className="text-[hsl(43_92%_56%)]">Education</span> &amp; Insights
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              In-depth guides, broker reviews, and beginner strategies — written by traders who've been there.
            </p>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[hsl(43_92%_56%)] font-medium hover:gap-2.5 transition-all"
            data-testid="blog-section-view-all">
            View all articles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Cards grid */}
        <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            // Skeleton placeholders
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-white/[0.04]" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-20 bg-white/[0.08] rounded" />
                  <div className="h-5 w-full bg-white/[0.08] rounded" />
                  <div className="h-3 w-3/4 bg-white/[0.06] rounded" />
                </div>
              </div>
            ))
          ) : (
            items.map((p) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.id}
                className="group block rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden transition-transform duration-200 hover:-translate-y-1 hover:border-white/15"
                data-testid={`blog-preview-${p.slug}`}
              >
                <div className="aspect-[16/9] bg-white/[0.04] overflow-hidden">
                  {p.cover_image ? (
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <BookOpen className="h-10 w-10 opacity-30" />
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
                    {p.category && <span className="text-[hsl(43_92%_56%)] font-semibold">{p.category}</span>}
                    <span className="text-muted-foreground/60">·</span>
                    <span className="text-muted-foreground">{fmtDate(p.published_at)}</span>
                  </div>
                  <h3 className="text-base font-bold leading-snug group-hover:text-[hsl(43_92%_60%)] transition-colors line-clamp-2">
                    {p.title}
                  </h3>
                  {p.summary && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{p.summary}</p>
                  )}
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1">
                    {p.reading_minutes && (
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.reading_minutes} min read</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[hsl(43_92%_56%)] font-medium ml-auto group-hover:gap-2 transition-all">
                      Read <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>

        {/* Mobile-friendly secondary CTA */}
        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[hsl(43_92%_56%)]/10 text-[hsl(43_92%_56%)] px-5 py-2 text-sm font-medium hover:bg-[hsl(43_92%_56%)]/15 transition-colors"
            data-testid="blog-section-view-all-mobile"
          >
            <BookOpen className="h-4 w-4" /> Browse all articles <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
